import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { readStorage, storageKey, subscribeStorage, writeStorage } from "./storage";

/** Always-fresh reference to a value (for stable callbacks). */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

/** Debounced copy of a value (search inputs → queries). */
export function useDebounce<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/** Debounced callback; the latest arguments win. */
export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, delayMs = 250) {
  const fnRef = useLatest(fn);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return useCallback(
    (...args: A) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => fnRef.current(...args), delayMs);
    },
    [delayMs, fnRef],
  );
}

/**
 * JSON value persisted in localStorage (namespaced "l2a:"), synced across tabs.
 * Falls back to in-memory state when storage is unavailable.
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, (next: T | ((prev: T) => T)) => void] {
  const k = storageKey(key);
  const initialRef = useRef(initialValue);
  const cache = useRef<{ raw: string | null; value: T } | null>(null);

  const getSnapshot = useCallback((): T => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(k);
    } catch {
      raw = null;
    }
    if (cache.current && cache.current.raw === raw) return cache.current.value;
    const value = raw === null ? initialRef.current : readStorage<T>(k, initialRef.current);
    cache.current = { raw, value };
    return value;
  }, [k]);

  const subscribe = useCallback(
    (onChange: () => void) =>
      subscribeStorage((changed) => {
        if (changed === k) onChange();
      }),
    [k],
  );

  const value = useSyncExternalStore(subscribe, getSnapshot, () => initialRef.current);
  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved = typeof next === "function" ? (next as (p: T) => T)(getSnapshot()) : next;
      writeStorage(k, resolved);
    },
    [k, getSnapshot],
  );
  return [value, setValue];
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** < 1024px: sidebar becomes a drawer. */
export function useIsMobile(): boolean {
  return !useMediaQuery("(min-width: 1024px)");
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

// ---------------------------------------------------------------------------
// Hotkeys
// ---------------------------------------------------------------------------

export const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

/** Display label for a hotkey, e.g. "mod+k" → ["⌘", "K"] on macOS, ["Ctrl", "K"] elsewhere. */
export function hotkeyLabel(combo: string): string[] {
  return combo
    .split(" ")
    .flatMap((step, i, all) => {
      const keys = step.split("+").map((k) => {
        switch (k) {
          case "mod":
            return IS_MAC ? "⌘" : "Ctrl";
          case "shift":
            return IS_MAC ? "⇧" : "Shift";
          case "alt":
            return IS_MAC ? "⌥" : "Alt";
          case "ctrl":
            return "Ctrl";
          case "meta":
            return IS_MAC ? "⌘" : "Win";
          case "enter":
            return "Enter";
          case "escape":
            return "Esc";
          case "arrowup":
            return "↑";
          case "arrowdown":
            return "↓";
          default:
            return k.length === 1 ? k.toUpperCase() : k[0]!.toUpperCase() + k.slice(1);
        }
      });
      return i < all.length - 1 ? [...keys, "then"] : keys;
    });
}

interface ParsedCombo {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
}

function parseCombo(combo: string): ParsedCombo {
  const parts = combo.toLowerCase().split("+");
  const key = parts.pop() ?? "";
  return {
    key: key === "space" ? " " : key,
    mod: parts.includes("mod"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
    ctrl: parts.includes("ctrl"),
    meta: parts.includes("meta"),
  };
}

function matches(e: KeyboardEvent, c: ParsedCombo): boolean {
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
  if (key !== c.key) return false;
  const wantMeta = c.meta || (c.mod && IS_MAC);
  const wantCtrl = c.ctrl || (c.mod && !IS_MAC);
  if (e.metaKey !== wantMeta || e.ctrlKey !== wantCtrl || e.altKey !== c.alt) return false;
  // Shift is only enforced when explicitly requested for letter keys ("?" already implies shift).
  if (c.shift && !e.shiftKey) return false;
  if (!c.shift && e.shiftKey && /^[a-z]$/.test(c.key)) return false;
  return true;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(type);
  }
  return target.closest("[role='textbox'],[role='combobox']") !== null;
}

export interface HotkeyOptions {
  enabled?: boolean;
  /** Fire even while the user types in an input (use for mod+k, escape). */
  allowInInputs?: boolean;
  preventDefault?: boolean;
}

/**
 * Global keyboard shortcuts. `keys` supports combos ("mod+k", "shift+?", "/", "escape")
 * and two-step sequences separated by a space ("g c" = press g, then c within 1s).
 */
export function useHotkeys(keys: string | readonly string[], handler: (e: KeyboardEvent) => void, options: HotkeyOptions = {}): void {
  const { enabled = true, allowInInputs = false, preventDefault = true } = options;
  const handlerRef = useLatest(handler);
  const list = useMemo(() => (Array.isArray(keys) ? [...keys] : [keys as string]), [keys]);
  const signature = list.join("|");

  useEffect(() => {
    if (!enabled) return;
    const sequences = list.map((k) => k.trim().split(/\s+/).map(parseCombo));
    let pending: { index: number; step: number; at: number }[] = [];

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented && !allowInInputs) return;
      if (e.isComposing || e.repeat) return;
      if (!allowInInputs && isTypingTarget(e.target)) return;
      const now = Date.now();
      pending = pending.filter((p) => now - p.at < 1000);

      // Continue sequences in progress.
      for (const p of pending) {
        const seq = sequences[p.index]!;
        const next = seq[p.step];
        if (next && matches(e, next)) {
          if (p.step === seq.length - 1) {
            if (preventDefault) e.preventDefault();
            pending = [];
            handlerRef.current(e);
            return;
          }
          p.step += 1;
          p.at = now;
          return;
        }
      }

      for (let i = 0; i < sequences.length; i++) {
        const seq = sequences[i]!;
        if (seq[0] && matches(e, seq[0])) {
          if (seq.length === 1) {
            if (preventDefault) e.preventDefault();
            handlerRef.current(e);
            return;
          }
          pending.push({ index: i, step: 1, at: now });
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, allowInInputs, preventDefault, signature]);
}

/** Calls `onVisible` when the sentinel element scrolls into view (infinite lists). */
export function useInView<T extends Element>(onVisible: () => void, options: { enabled?: boolean; rootMargin?: string } = {}) {
  const { enabled = true, rootMargin = "400px" } = options;
  const ref = useRef<T | null>(null);
  const cb = useLatest(onVisible);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) cb.current();
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, rootMargin, cb]);
  return ref;
}

/** Re-render periodically (relative timestamps). */
export function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
